<style>
  .columns {
    align-items: center;
    display: flex;
    flex-flow: row;
  }
  .do {
    flex: 1 1 50%;
    margin: 0.6em 0.4em 0.2em 0.4em; /* top | right | bottom | left */
  }
  .file-input {
    display: none;
  }
  .upload-status {
    
  }
  .upload-failure {
    color: #a00;
  }
  .upload-progress {
    color: #c80;
  }
  .upload-success {
    color: #373;
  }
</style>

<script>
  import { afterUpdate, onDestroy, onMount } from 'svelte';
  import config from '../../config';
  import {
    auth_client, auth_logged_in, auth_user,
    bookmarks, bookmark_selected,
    lang, leaflet_map,
    panel_selected,
    plots, plot_creation_mode, plot_selected,
    prof_edit, prof_edit_mode,
    upload_status_code, upload_status_text,
  } from '../store';
  import auth from '../auth';
  import sync from '../sync';
  import util from '../util';
  
  function get_next_seq(obj) {
    const ids = Object.keys(obj).sort();
    let next_seq = 0;
    if (ids.length > 0) {
      next_seq = Number(ids[ids.length - 1]) + 1;
    }
    return next_seq;
  }
  
  async function add_bookmark() {
    const new_seq = get_next_seq($bookmarks);
    const new_seq_str = String(new_seq).padStart(config.bookmarks.seq_length, '0');
    const lat_long = $leaflet_map.getCenter();
    const new_name = util.caption($lang, 'bookm_panel', 'new_name');
    const bookmark = {
      bookm_name: `${new_name} ${new_seq + 1}`,
      bookm_seq: new_seq,
      lat: lat_long.lat,
      long: lat_long.lng,
      zoom: $leaflet_map.getZoom(),
    };
    if ($auth_logged_in) {
      await sync.update_bookmark(bookmark, 'create');
    } else {
      bookmark.bookm_id = new_seq_str;
      $bookmarks[new_seq_str] = bookmark;
      $bookmarks = $bookmarks;
    }
    if (new_seq_str in $bookmarks) {
      $bookmark_selected = $bookmarks[new_seq_str].bookm_id;
    }
  }
  async function add_plot_cancel() {
    $plot_creation_mode = false;
    enable_geom_creation(false);
  }
  async function add_plot_save(layer) {
    $plot_creation_mode = false;
    enable_geom_creation(false);
    const plot_nr = Object.keys($plots).length + 1;
    const new_name = util.caption($lang, 'plot_panel', 'new_name');
    const geojson = layer.toGeoJSON(6).geometry;
    
    let plot = {
      geojson: geojson,
      layer: layer,
      plot_name: `${new_name} ${plot_nr}`,
    };
    
    let plot_seq = await sync.update_plot(plot, 'create');
    // trying to assign a cadastre number to new plot
    if (plot_seq in $plots) {
      plot = $plots[plot_seq];
      if (plot.point_inside) {
        const cad_info = await util.get_cad_info(plot.point_inside);
        if (cad_info && cad_info.code) {
          plot.cad_info = cad_info;
          plot.cad_nr = cad_info.code;
          plot.plot_name = cad_info.code;
          plot_seq = await sync.update_plot(plot, 'update');
        }
      }
    }
    // adding plot to plot panel
    if (plot_seq in $plots) {
      $plot_selected = plot.plot_seq;
    }
    // opening plot panel if user is on mobile
    if (window.innerWidth < 800) {
      $panel_selected = 'plots';
    }
  }
  async function add_plot_start() {
    $plot_creation_mode = true;
    enable_geom_creation(true);
    if (window.innerWidth < 800) {
      $panel_selected = '';
    }
  }
  function edit_cancel() {
    $prof_edit_mode = false;
  }
  function edit_profile() {
    $prof_edit.input_selected_once = false;
    $prof_edit.input_valid = false;
    $prof_edit.result = {};
    $prof_edit.validation = {};
    $prof_edit = $prof_edit;
    $prof_edit_mode = true;
  }
  function edit_save() {
    let changes = {};
    for (const [key, value] of Object.entries($prof_edit.result)) {
      if ($auth_user[key] !== value) {
        changes[key] = value;
      }
    }
    //if (dev) {console.log(changes);}
    if (Object.keys(changes).length) {
      sync.update_user(changes);
    }
    $prof_edit_mode = false;
  }
  function enable_geom_creation(enable) {
    if (enable) {
      enable_measure(false);
      $leaflet_map.on('pm:create', event => {
        enable_geom_creation(false);
        add_plot_save(event.layer);
      });
      $leaflet_map.pm.enableDraw('Polygon', {allowSelfIntersection: false});
    } else {
      $leaflet_map.off('pm:create');
      $leaflet_map.pm.disableDraw();
      enable_measure(true);
    }
  }
  function enable_measure(enable) {
    if (enable) {
      $leaflet_map.on('pm:create', event => {
        event.layer.showMeasurements({
          formatArea: function(value) {
            value = value / 10000;
            return `${value.toFixed(2)} ha`;
          },
        });
      });
      if (!$leaflet_map.pm.controlsVisible()) {
        $leaflet_map.pm.addControls(config.geoman.measure_controls);
      }
    } else {
      if ($leaflet_map.pm.controlsVisible()) {
        $leaflet_map.pm.removeControls();
      }
      $leaflet_map.off('pm:create');
    }
  }
  async function login() {
    await auth.login({redirect_uri: window.location.href});
  }
  async function logout() {
    await auth.logout({returnTo: window.location.href});
  }
  async function run_action() {
    if (button_id === 'add_bookmark') {
      await add_bookmark();
    } else {
      console.error(`Unknown button_id ${button_id}`);
    }
  }
  function upload_file_open() {
    file_input.click();
  }
  async function upload_file_start(event) {
    if (!event.target.files || !event.target.files.length) {
      return;
    }
    $upload_status_code = 'upload-progress';
    $upload_status_text = util.caption($lang, 'upload', 'started');
    let total_size = 0;
    let files = [];
    for (const file of event.target.files) {
      if (!file.size || file.size === 0 || file.size > 1073741824) {
        $upload_status_code = 'upload-failure';
        $upload_status_text = util.caption($lang, 'upload', 'failure_size');
        total_size = 0;
        break;
      } else if (file.type !== 'application/zip' && file.type !== 'application/x-zip-compressed') {
        console.error(`Unsupported upload type ${file.type}`);
        $upload_status_code = 'upload-failure';
        $upload_status_text = util.caption($lang, 'upload', 'failure_type');
        total_size = 0;
        break;
      } else {
        total_size = total_size + file.size;
        files.push(file);
      }
    }
    if (total_size && files.length) {
      sync.upload_files(files, total_size, $lang);
    }
  }
  
  // afterUpdate(() => {
  //   if ($plot_creation_mode && button_id !== 'add_plot') {
  //     $plot_creation_mode = false;
  //     enable_geom_creation(false);
  //   }
  // });
  
  onDestroy(() => {
    // if ($plot_creation_mode) {
    //   $plot_creation_mode = false;
    //   enable_geom_creation(false);
    // }
  });
  
  onMount(() => {
    $prof_edit.edit_cancel = edit_cancel;
    $prof_edit.edit_save = edit_save;
  });
  
  export let button_id;
  
  let file_input;
</script>

{#if button_id === 'add_plot'}
  {#if $auth_logged_in}
    {#if $plot_creation_mode}
  <!--     <div class='columns'>
        <button class='orange' on:click={add_plot_save}>{util.caption($lang,'plot_buttons','save')}</button> -->
        <button class='orange' on:click={add_plot_cancel}>{util.caption($lang,'plot_buttons','cancel')}</button>
      <!-- </div> -->
    {:else}
      <button on:click={add_plot_start}>{util.caption($lang,'plot_buttons','start')}</button>
    {/if}
  {:else}
    <p>{util.caption($lang,'plot_buttons','login_signup')}</p>
    <button on:click={login}>{@html util.caption($lang,'panel_buttons','login')}</button>
  {/if}
{:else if button_id === 'edit_profile'}
  {#if $auth_logged_in}
    {#if $prof_edit_mode}
      <div class='columns'>
        {#if $prof_edit.input_valid}
          <button class='do orange' on:click={edit_save}>{util.caption($lang,'prof_panel','edit_save')}</button>
        {:else}
          <button class='do orange' disabled>{util.caption($lang,'prof_panel','edit_save')}</button>
        {/if}
        <button class='do green' on:click={edit_cancel}>{util.caption($lang,'prof_panel','edit_cancel')}</button>
      </div>
    {:else}
      <button class='do orange' on:click={edit_profile}>{util.caption($lang,'prof_panel','edit_profile')}</button>
    {/if}
  {/if}
{:else if button_id === 'login_logout'}
  {#if $auth_logged_in}
    <button on:click={logout}>{util.caption($lang,'panel_buttons','logout')}</button>
  {:else}
    <button on:click={login}>{@html util.caption($lang,'panel_buttons','login')}</button>
  {/if}
{:else if button_id === 'upload_file'}
  {#if $auth_logged_in}
    {#if $upload_status_code}
      <p class='upload-status {$upload_status_code}'>{$upload_status_text}</p>
    {/if}
    {#if $upload_status_code === 'upload-progress'}
      <button disabled>{util.caption($lang,'panel_buttons','upload_start')}</button>
    {:else}
      <button on:click={upload_file_open}>{util.caption($lang,'panel_buttons','upload_start')}</button>
    {/if}
    <input accept='application/zip' bind:this={file_input} class='file-input' multiple on:change={upload_file_start} type='file' />
  {/if}
{:else if button_id === 'empty'}
{:else}
  <button on:click={run_action}>{util.caption($lang,'panel_buttons',button_id)}</button>
{/if}
